/**
 * Pure helpers behind the spoke-staff attendance screens. No React, no store,
 * no native — everything here is unit-tested in __tests__/attendance.test.js.
 */

export const ATTENDANCE_UPLOAD_PATH = "leader/attendance/capture";

export const ATTENDANCE_ROLES = Object.freeze({
  EXECUTIVE: "CMO_EXECUTIVE",
  JR_EXECUTIVE: "CMO_JR_EXECUTIVE",
});

export const EVENT_TYPES = Object.freeze({ IN: "IN", OUT: "OUT" });

export const ROLE_STATUS = Object.freeze({
  NOT_MARKED: "NOT_MARKED",
  IN_MARKED: "IN_MARKED",
  COMPLETE: "COMPLETE",
});

/** The subject token lives 30 min server-side; re-identify before it can lapse mid-capture. */
export const SUBJECT_TOKEN_MAX_AGE_MS = 25 * 60 * 1000;

const IST_OFFSET_MS = 330 * 60 * 1000;
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/** Calendar date in IST as YYYY-MM-DD. Punches are "per day" in the spoke's day, not UTC's. */
export const todayIST = (now = new Date()) =>
  new Date(now.getTime() + IST_OFFSET_MS).toISOString().slice(0, 10);

/** "2026-08-21" -> "Fri, 21 Aug". Empty string for anything unparseable. */
export const formatAttendanceDate = (date) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(date ?? ""));
  if (!match) return "";
  const [, y, m, d] = match;
  const utc = new Date(Date.UTC(Number(y), Number(m) - 1, Number(d)));
  if (Number.isNaN(utc.getTime())) return "";
  return `${WEEKDAYS[utc.getUTCDay()]}, ${Number(d)} ${MONTHS[utc.getUTCMonth()]}`;
};

/** ISO instant -> "h:mm AM/PM" in IST (device timezone independent). Empty string if invalid. */
export const formatClockTime = (iso) => {
  const ms = Date.parse(iso ?? "");
  if (Number.isNaN(ms)) return "";
  const ist = new Date(ms + IST_OFFSET_MS);
  const hours = ist.getUTCHours();
  const minutes = String(ist.getUTCMinutes()).padStart(2, "0");
  const suffix = hours >= 12 ? "PM" : "AM";
  return `${hours % 12 || 12}:${minutes} ${suffix}`;
};

/** "9h 05m" between two ISO instants; null unless both parse and out >= in. */
export const formatShiftDuration = (inIso, outIso) => {
  const inMs = Date.parse(inIso ?? "");
  const outMs = Date.parse(outIso ?? "");
  if (Number.isNaN(inMs) || Number.isNaN(outMs) || outMs < inMs) return null;
  const totalMinutes = Math.floor((outMs - inMs) / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = String(totalMinutes % 60).padStart(2, "0");
  return `${hours}h ${minutes}m`;
};

/** IN/OUT status for a subject from an identify / capture response. */
export const roleStatusFromEvents = ({ in: inEvent, out: outEvent } = {}) => {
  if (inEvent && outEvent) return ROLE_STATUS.COMPLETE;
  if (inEvent) return ROLE_STATUS.IN_MARKED;
  return ROLE_STATUS.NOT_MARKED;
};

/**
 * Subtitle for a role tile on MarkAttendance. A tile is the entry point for
 * marking anyone in that role, so it shows how many people this spoke marked
 * today, never one person's status.
 */
export const roleTileSubtitle = (entry) => {
  const inCount = entry?.marked_today?.in ?? 0;
  const outCount = entry?.marked_today?.out ?? 0;
  if (inCount === 0) return "Tap to mark attendance";
  return `Today: ${inCount} In · ${outCount} Out`;
};

/** Whether the cached subject token is too old to trust for a capture. */
export const isSubjectTokenStale = (issuedAtMs, now = Date.now()) =>
  !Number.isFinite(issuedAtMs) || now - issuedAtMs >= SUBJECT_TOKEN_MAX_AGE_MS;

/**
 * Key under which the pending client_event_id is persisted: one per subject,
 * IST day and event type. Deliberately not the subject token, which changes on
 * every identify — a relaunch after re-identifying must reuse the same key.
 */
export const pendingEventKey = ({ role, displayId, date, eventType }) =>
  [role, displayId, date, eventType].join(":");

/**
 * Drops pending client_event_ids from earlier IST days. A capture closed
 * without submitting leaves its key behind; once the day is over it can never
 * be reused (the server keys IN/OUT by day), so it is dead weight in storage.
 */
export const prunePendingEvents = (pending, today) => {
  const out = {};
  Object.keys(pending || {}).forEach((key) => {
    const parts = key.split(":");
    const date = parts[parts.length - 2];
    if (date && date >= today) out[key] = pending[key];
  });
  return out;
};

const numOrNull = (v) =>
  typeof v === "number" && Number.isFinite(v) ? v : null;

/**
 * The react-native-face-capture launch config for one mark. The SDK adds the
 * JPEG and `meta_data`; everything that identifies the request is set here.
 */
export const buildAttendanceLaunchConfig = ({
  eventType,
  subjectToken,
  token,
  baseUrl,
  appVersion,
  appVersionCode,
  clientEventId,
  fix,
  capturedAt,
}) => ({
  title:
    eventType === EVENT_TYPES.OUT
      ? "Mark Out Attendance"
      : "Mark In Attendance",
  canClose: true,
  captureMode: "auto",
  // tl-app has no boot-time camera gate; the screen asks once itself.
  requestPermission: true,
  upload: {
    url: `${String(baseUrl || "").replace(/\/+$/, "")}/${ATTENDANCE_UPLOAD_PATH}`,
    headers: {
      Authorization: token,
      "x-app-name": "CL",
      "x-app-version": String(appVersion ?? ""),
      "x-app-version-code": String(appVersionCode ?? ""),
    },
    fields: {
      subject_token: subjectToken,
      event_type: eventType,
      client_event_id: clientEventId,
      captured_at: capturedAt,
      app_version: String(appVersion ?? ""),
      device: "android",
      lat: numOrNull(fix?.lat),
      lng: numOrNull(fix?.lng),
      accuracy_m: numOrNull(fix?.accuracy),
    },
  },
});
